import { memo } from 'react';

import { ApplicationScoreOverview } from '../../ApplicationScoreOverview/ApplicationScoreOverview';
import { ApplicationValidationControls } from './ApplicationValidationControls/ApplicationValidationControls';
import type { VotesTableModel } from './ApplicationValidationControls/ApplicationValidationVotesModal/ApplicationValidationVotesModal';
import { ApplicationValidationDetails } from './ApplicationValidationDetails/ApplicationValidationDetails';
import { ApplicationValidationScore } from './ApplicationValidationScore/ApplicationValidationScore';

export type IsValidationFinished = {
  /**
   * Indicates whether the validation period has finished
   */
  isValidationFinished?: boolean;
};

export type IsSqrvVotingPowerUsed = {
  /**
   * Indicates whether SQRv voting power should be used instead of regular voting power
   */
  isSqrvVotingPowerUsed?: boolean;
};

export type VoteStats = {
  approvedPercentage?: number;
  rejectedPercentage?: number;
  totalVotes?: number;
};

export type ValidationPeriod = {
  startDate?: string;
  endDate?: string;
  formattedEndDate?: string;
};

export type ScoreProps = {
  /**
   * Score value
   */
  value?: number;
  /**
   * Maximum possible score value
   */
  maxValue?: number;
  /**
   * Number of votes casted by users
   */
  votes?: number;
  /**
   * Entity type (Votes, Reviews)
   */
  entityType?: string;
};

export type DetailsProps = {
  /**
   * Overall vote stats information including approval percentage, rejection percentage, and total votes
   */
  voteStats?: VoteStats;
  /**
   * Overall validation period information including start date, end date, and formatted end date
   */
  validationPeriod?: ValidationPeriod;
  /**
   * User's voting power
   */
  userVotingPower?: number | null;
  /**
   * Total SQRv voting power before validation period ends
   */
  preSnapshotSqrvVotingPower?: number;
};

export type ControlsProps = {
  /**
   * Indicates whether the current user has already voted
   */
  hasVoted?: boolean;
  /**
   * Callback function when a user clicks the Vote on Project button
   */
  onVote?: () => void;
  /**
   * Callback function when a user clicks the Details button
   */
  onShowDetails?: () => void;
  /**
   * Overall votes table model information including data, loading state, and pagination configuration
   */
  votesTableModel?: VotesTableModel;
};

export interface ApplicationValidationSummaryProps
  extends IsValidationFinished,
    IsSqrvVotingPowerUsed {
  /**
   * Application name to display in the title
   */
  appName?: string;
  /**
   * Overall score information including value, max value, and vote count
   */
  scoreProps?: ScoreProps;
  /**
   * Overall validation period information including start date, end date, and formatted end date
   */
  detailsProps?: DetailsProps;
  /**
   * Overall validation controls information including voting state, callback functions for showing details and voting, and votes table model
   */
  controlsProps?: ControlsProps;
  /**
   * Indicates whether the component is currently loading
   * @default false
   */
  isLoading?: boolean;
}

export const ApplicationValidationSummary = memo<ApplicationValidationSummaryProps>(
  ({
    appName = 'App',
    scoreProps,
    detailsProps,
    controlsProps,
    isSqrvVotingPowerUsed,
    isValidationFinished,
    isLoading = false,
  }) => (
    <ApplicationScoreOverview>
      <ApplicationScoreOverview.Title>
        {`${appName} Validation Score in Magic Store`}
      </ApplicationScoreOverview.Title>
      <ApplicationScoreOverview.Score>
        <ApplicationValidationScore {...scoreProps} />
      </ApplicationScoreOverview.Score>
      <ApplicationScoreOverview.Controls>
        <ApplicationValidationControls
          isSqrvVotingPowerUsed={isSqrvVotingPowerUsed}
          isValidationFinished={isValidationFinished}
          isLoading={isLoading}
          {...controlsProps}
        />
      </ApplicationScoreOverview.Controls>
      <ApplicationScoreOverview.Details>
        <ApplicationValidationDetails
          isSqrvVotingPowerUsed={isSqrvVotingPowerUsed}
          isValidationFinished={isValidationFinished}
          {...detailsProps}
        />
      </ApplicationScoreOverview.Details>
    </ApplicationScoreOverview>
  ),
);

ApplicationValidationSummary.displayName = 'ApplicationValidationSummary';
